import React, { useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import SvgIcon from '@material-ui/core/SvgIcon';
import {
  TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL,
  TEACHERS_HOME_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { useAssignmentResults, useTrackerInstanceSectionDetail } from 'sdk';

import GradeOverview from 'pages/Teachers/Insights/components/GradeOverview';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import { useNavigation, useSections } from 'pages/Teachers/shared/hooks';
import { parseParams, reverse, stringifyParams } from 'utils/urls';

import TrackerInstanceSectionContext from 'components/TrackerInstanceSectionContext';

import { ReactComponent as MainIcon } from './assets/edlightMain.svg';
import styles from './styles.module.scss';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';

const Insights = ({ history, width }) => {
  const { trackerInstanceSectionId } = useParams();
  const location = useLocation();

  const { data: trackerInstanceSection } = useTrackerInstanceSectionDetail({
    trackerInstanceSectionId
  });

  const trackerInstanceSectionData = {
    standards: trackerInstanceSection?.standards.map(
      (standard) => standard.code
    ),
    class_id: trackerInstanceSection?.section_id
  };

  const {
    data: assignment,
    mutate: setAssignment,
    isLoading: loadingAssignment
  } = useAssignmentResults(trackerInstanceSection?.id);

  const sections = useSections(trackerInstanceSection?.tracker_id);

  const updateQueryParam = useCallback(
    (newTrackerInstanceSectionId) => {
      history.push({
        pathname: reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
          trackerInstanceSectionId: newTrackerInstanceSectionId
        }),
        search: stringifyParams(
          parseParams(location.search, {
            parseNumbers: true
          })
        )
      });
    },
    [location, history]
  );

  useNavigation({
    assignment,
    selectedSectionId: trackerInstanceSection?.id,
    sections,
    selectSection: updateQueryParam
  });

  const handleTitleClick = useCallback(() => {
    if (location.state?.from === TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL) {
      if (HOME_PAGE_ENABLED) {
        history.push({
          pathname: TEACHERS_HOME_URL
        });
      } else {
        history.push(TEACHER_ASSIGNMENTS_V2_URL);
      }
    } else {
      history.goBack();
    }
  }, [history, location.state]);

  useNavbar({
    title: '',
    handleTitleClick,
    sticky: true,
    onBackButtonClick: handleTitleClick
  });

  const params = parseParams(location.search, {
    parseNumbers: true
  });

  const defaultWorkId = params['workId'];
  const defaultTaskId = params['taskId'];
  const defaultStudentId = params['studentId'];
  const isFeedbackOpened = params['isFeedbackOpened'] === 'true';

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          {trackerInstanceSection?.standards.map((standard, index) => (
            <div key={index} className={styles.standardsContainer}>
              <SvgIcon component={MainIcon} />
              <span>
                {standard['code']} - {standard['short_description']}
              </span>
            </div>
          ))}
        </div>
      </div>

      <TrackerInstanceSectionContext.Provider
        value={trackerInstanceSectionData}
      >
        <div className={styles.detailContainer}>
          {assignment && (
            <GradeOverview
              trackerId={trackerInstanceSection?.tracker_id}
              width={width}
              assignment={assignment}
              setAssignment={setAssignment}
              loadingAssignment={loadingAssignment}
              studentDeepLink={trackerInstanceSection?.student_deep_link}
              selectedSectionId={trackerInstanceSection?.id}
              defaultStudentId={defaultStudentId}
              defaultWorkId={defaultWorkId}
              defaultTaskId={defaultTaskId}
              isFeedbackOpened={isFeedbackOpened}
            />
          )}
        </div>
      </TrackerInstanceSectionContext.Provider>
    </div>
  );
};

export default Insights;
